// Shared API utility functions
import { offlineApiQueue } from './offlineApiQueue';
import { imageCacheManager } from './imageCacheManager';

const FRAPPE_URL = process.env.REACT_APP_FRAPPE_URL || 'http://pra.localhost:8000';
const API_TIMEOUT = 10000; // 10 second timeout for API calls

/**
 * Attempt to refresh the session by making a lightweight API call
 * This can re-establish the sid cookie if it expired but user is still "logged in"
 * @returns {Promise<boolean>} - true if session refreshed successfully
 */
const refreshSession = async () => {
  try {
    console.log('🔄 Attempting to refresh session...');
    
    // Make a simple API call that should refresh the sid cookie
    const response = await fetch(`${FRAPPE_URL}/api/method/frappe.auth.get_logged_user`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json'
      }
    });
    
    if (response.ok) {
      console.log('✅ Session refreshed successfully');
      return true;
    } else {
      console.warn('⚠️ Session refresh failed with status:', response.status);
      return false;
    }
  } catch (error) {
    console.error('❌ Session refresh error:', error);
    return false;
  }
};

/**
 * Handle session expiry - try to refresh first, only logout if refresh fails
 */
const handleSessionExpired = async () => {
  console.warn('⚠️ Session appears expired - attempting to refresh...');
  
  const refreshed = await refreshSession();
  
  if (refreshed) {
    console.log('✅ Session refreshed - continuing...');
    return true; // Session was refreshed successfully
  } else {
    console.error('❌ Session refresh failed - redirecting to login');
    
    // Clear all authentication data
    localStorage.removeItem('frappe_user');
    localStorage.removeItem('frappe_logged_in');
    localStorage.removeItem('frappe_user_line');
    
    // Reload to show login page
    window.location.reload();
    return false;
  }
};

/**
 * Fetch with timeout and automatic backend availability detection
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<Response>}
 */
const fetchWithTimeout = async (url, options = {}, timeout = API_TIMEOUT) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    
    // If request succeeded, mark backend as available
    if (response.ok || response.status < 500) {
      offlineApiQueue.setBackendAvailable(true);
    } else if (response.status >= 500) {
      // 5xx errors indicate backend issues
      console.warn(`⚠️ Backend returned ${response.status} error`);
      offlineApiQueue.setBackendAvailable(false);
    }
    
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    
    // Check if it's a network error or timeout
    if (error.name === 'AbortError') {
      console.warn('⏱️ Request timed out - backend may be unavailable');
      offlineApiQueue.setBackendAvailable(false);
      throw new Error('Request timeout - backend unavailable');
    } else if (error.message.includes('fetch') || error.message.includes('NetworkError')) {
      console.warn('❌ Network error - backend unavailable');
      offlineApiQueue.setBackendAvailable(false);
      throw new Error('Network error - backend unavailable');
    }
    
    throw error;
  }
};

/**
 * Transform flat item_and_defect_properties array into grouped structure
 * Groups defects by item and creates defect_areas array
 */
const transformItemDefectProperties = (itemAndDefectProperties) => {
  if (!Array.isArray(itemAndDefectProperties)) {
    return [];
  }

  // Group by item
  const itemsMap = {};
  
  itemAndDefectProperties.forEach(entry => {
    const itemName = entry.item;
    
    if (!itemsMap[itemName]) {
      itemsMap[itemName] = {
        item: itemName,
        image: entry.image,
        brand_logo: entry.brand_logo,
        customer_logo: entry.customer_logo,
        defect_areas: []
      };
    } else {
      // Update logos if they exist in this entry and weren't set before
      if (entry.brand_logo && !itemsMap[itemName].brand_logo) {
        itemsMap[itemName].brand_logo = entry.brand_logo;
      }
      if (entry.customer_logo && !itemsMap[itemName].customer_logo) {
        itemsMap[itemName].customer_logo = entry.customer_logo;
      }
      // Update image if it exists and wasn't set before
      if (entry.image && !itemsMap[itemName].image) {
        itemsMap[itemName].image = entry.image;
      }
    }
    
    // Only add defect area if it has valid data
    if (entry.area && entry.x_coordinate != null && entry.y_coordinate != null && entry.possible_defect) {
      // Check if this area already exists for this item
      const existingArea = itemsMap[itemName].defect_areas.find(
        da => da.area === entry.area && 
              da.x_coordinates === entry.x_coordinate && 
              da.y_coordinates === entry.y_coordinate
      );
      
      // Create defect object with both name and ID
      const defectObj = {
        name: entry.possible_defect,
        id: entry.item_defect_name // This is the defect ID needed for the backend
      };
      
      if (existingArea) {
        // Add defect to existing area if not already present
        const existingDefect = existingArea.defects.find(d => d.id === defectObj.id);
        if (!existingDefect) {
          existingArea.defects.push(defectObj);
        }
      } else {
        // Create new defect area
        itemsMap[itemName].defect_areas.push({
          area: entry.area,
          x_coordinates: entry.x_coordinate,
          y_coordinates: entry.y_coordinate,
          defects: [defectObj]
        });
      }
    }
  });
  
  const result = Object.values(itemsMap);
  console.log('📋 Transformed item properties with logos:');
  result.forEach(item => {
    console.log(`  Item: ${item.item}`);
    console.log(`    brand_logo: ${item.brand_logo || 'NOT SET'}`);
    console.log(`    customer_logo: ${item.customer_logo || 'NOT SET'}`);
    console.log(`    image: ${item.image || 'NOT SET'}`);
  });
  
  return result;
};

/**
 * Fetch WIP details - backend will return line along with styles_wip and item_and_defect_properties
 */
export const fetchWIPDetails = async () => {
  try {
    const url = `${FRAPPE_URL}/api/method/pragyaa_apparels_erpnext_customizations.api.production_tracking.wip_details`;
    
    console.log('Fetching WIP details...');
    
    const response = await fetchWithTimeout(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      console.log('WIP details response:', data);
      
      // Extract the data from response
      const responseData = data?.message || data;
      
      if (responseData && responseData.styles_wip && Array.isArray(responseData.styles_wip)) {
        // Store line if returned by backend
        if (responseData.line) {
          localStorage.setItem('frappe_user_line', responseData.line);
          console.log('✓ User line number:', responseData.line);
        }
        
        // Transform item_and_defect_properties into the format expected by frontend
        const itemProperties = transformItemDefectProperties(responseData.item_and_defect_properties || []);
        
        // Store in localStorage
        localStorage.setItem('frappe_style_properties', JSON.stringify(responseData.styles_wip));
        localStorage.setItem('frappe_item_properties', JSON.stringify(itemProperties));
        
        // Store line_stats if available
        if (responseData.line_stats) {
          localStorage.setItem('frappe_line_stats', JSON.stringify(responseData.line_stats));
          console.log('✓ Successfully fetched line stats:', responseData.line_stats);
        }
        
        // Store defect_history if available
        if (responseData.defect_history) {
          localStorage.setItem('frappe_defect_history', JSON.stringify(responseData.defect_history));
          console.log('✓ Successfully fetched defect history:', responseData.defect_history);
        }
        
        console.log('✓ Successfully fetched style properties:', responseData.styles_wip);
        console.log('✓ Successfully fetched item properties:', itemProperties);
        
        // Cache images for offline use
        try {
          console.log('🖼️ Starting image caching process...');
          const imageUrls = imageCacheManager.extractImageUrls(responseData);
          
          if (imageUrls.length > 0) {
            console.log(`🖼️ Found ${imageUrls.length} images to cache:`, imageUrls);
            
            // Cache images in the background (don't block the response)
            imageCacheManager.cacheImages(imageUrls, (progress) => {
              console.log(`🖼️ Image caching progress: ${progress.cached}/${progress.total} (${Math.round((progress.cached/progress.total) * 100)}%)`);
            }).then((result) => {
              console.log(`✅ Image caching completed: ${result.cached}/${result.total} images cached successfully`);
              if (result.failed > 0) {
                console.warn(`⚠️ Failed to cache ${result.failed} images`);
              }
            }).catch((error) => {
              console.error('❌ Image caching failed:', error);
            });
          } else {
            console.log('📷 No images found to cache');
          }
        } catch (imageCacheError) {
          console.error('❌ Error in image caching process:', imageCacheError);
          // Don't fail the entire API call if image caching fails
        }
        
        return {
          success: true,
          line: responseData.line,
          styles_wip: responseData.styles_wip,
          item_properties: itemProperties,
          line_stats: responseData.line_stats,
          defect_history: responseData.defect_history
        };
      } else {
        console.warn('Received empty or invalid data');
        return { success: false };
      }
    } else {
      console.error('Failed to fetch WIP details. Status:', response.status);
      let errorText = '';
      let errorData = null;
      
      try {
        errorText = await response.text();
        console.error('Error response:', errorText);
        
        // Try to parse as JSON to get structured error
        try {
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          // Response is not JSON, keep as text
        }
      } catch (e) {
        console.error('Could not read error response body:', e);
        errorText = 'Unable to read error response';
      }
      
      // Return full backend error message
      return { 
        success: false, 
        error: `Server error (${response.status}): ${errorText}`,
        statusCode: response.status,
        errorData: errorData
      };
    }
  } catch (error) {
    console.error('Error fetching WIP details:', error);
    return { success: false };
  }
};

/**
 * Insert output data when pass button is clicked
 */
export const insertOutputData = async (salesOrder, item, colour, fit, size, postingDatetime, repaired = 0) => {
  // Check if offline - queue the call if so
  if (offlineApiQueue.shouldUseOfflineMode()) {
    const queueId = offlineApiQueue.queueApiCall('insertOutputData', {
      salesOrder,
      item,
      colour,
      fit,
      size,
      postingDatetime,
      repaired
    });
    
    console.log('📵 Offline: Queued output data insertion with ID:', queueId);
    return {
      success: true,
      queued: true,
      queueId,
      message: 'API call queued for when online'
    };
  }

  try {
    const url = `${FRAPPE_URL}/api/method/pragyaa_apparels_erpnext_customizations.api.production_tracking.insert_output_data`;
    
    const requestData = {
      sales_order: salesOrder,
      item,
      colour,
      fit,
      size,
      posting_datetime: postingDatetime,
      output_qty: 1,
      repaired
    };
    
    console.log('🚀 Inserting output data...');
    console.log('📍 URL:', url);
    console.log('📦 Request data:', requestData);
    
    // Frappe REST API uses cookie-based authentication (sid cookie), not CSRF tokens
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
    
    let response = await fetchWithTimeout(url, {
      method: 'POST',
      headers,
      credentials: 'include', // This sends the sid cookie for authentication
      body: JSON.stringify(requestData)
    });

    console.log('📡 Response status:', response.status);
    console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

    // Handle 401/403 by attempting session refresh and retry
    if (response.status === 401 || response.status === 403) {
      console.warn('⚠️ Received 401/403 - attempting session refresh and retry...');
      
      const refreshed = await handleSessionExpired();
      
      if (refreshed) {
        // Session refreshed successfully - retry the request
        console.log('🔄 Retrying request after session refresh...');
        response = await fetchWithTimeout(url, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(requestData)
        });
        
        console.log('📡 Retry response status:', response.status);
      } else {
        // Session refresh failed, user will be logged out
        return { success: false, error: 'Session expired', sessionExpired: true };
      }
    }

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Output data inserted successfully:', data);
      
      return {
        success: true,
        data: data?.message || data
      };
    } else {
      console.error('❌ Failed to insert output data. Status:', response.status);
      console.error('❌ Response status text:', response.statusText);
      
      let errorText;
      let errorData = null;
      try {
        errorText = await response.text();
        console.error('❌ Error response body:', errorText);
        
        // Try to parse as JSON to get more details
        try {
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          // Response is not JSON, keep as text
        }
      } catch (e) {
        console.error('❌ Could not read error response body:', e);
        errorText = 'Unable to read error response';
      }
      
      // Check if it's specifically a CSRF error
      const isCSRFError = errorText.includes('CSRFTokenError') || 
                         errorText.includes('Invalid Request') ||
                         response.status === 403;
      
      // Return full backend error message
      return { 
        success: false, 
        error: `Server error (${response.status}): ${errorText}`,
        statusCode: response.status,
        errorData: errorData,
        isCSRFError: isCSRFError
      };
    }
  } catch (error) {
    console.error('❌ Network/fetch error inserting output data:', error);
    console.error('❌ Error name:', error.name);
    console.error('❌ Error message:', error.message);
    
    // If it's a network error or timeout, queue the request
    if (error.message.includes('backend unavailable') || 
        error.message.includes('timeout') || 
        error.message.includes('NetworkError')) {
      
      console.log('📵 Backend unavailable during request - queueing for retry');
      const queueId = offlineApiQueue.queueApiCall('insertOutputData', {
        salesOrder,
        item,
        colour,
        fit,
        size,
        postingDatetime,
        repaired
      });
      
      return {
        success: true,
        queued: true,
        queueId,
        message: 'Backend unavailable - API call queued for retry'
      };
    }
    
    return { 
      success: false, 
      error: error.message,
      errorType: error.name
    };
  }
};

/**
 * Insert defect data when defect is reported
 */
export const insertDefectData = async (salesOrder, item, colour, fit, size, outputQty, repairable, defectIds) => {
  // Check if offline - queue the call if so
  if (offlineApiQueue.shouldUseOfflineMode()) {
    const queueId = offlineApiQueue.queueApiCall('insertDefectData', {
      salesOrder,
      item,
      colour,
      fit,
      size,
      outputQty,
      repairable,
      defectIds
    });
    
    console.log('📵 Offline: Queued defect data insertion with ID:', queueId);
    return {
      success: true,
      queued: true,
      queueId,
      message: 'API call queued for when online'
    };
  }

  try {
    const url = `${FRAPPE_URL}/api/method/pragyaa_apparels_erpnext_customizations.api.production_tracking.insert_defect_data`;
    
    const requestData = {
      sales_order: salesOrder,
      item,
      colour,
      fit,
      size,
      output_qty: outputQty,
      repairable: repairable ? 1 : 0,
      defect_ids: defectIds
    };
    
    console.log('🚀 Inserting defect data...');
    console.log('📍 URL:', url);
    console.log('📦 Request data:', requestData);
    
    // Frappe REST API uses cookie-based authentication (sid cookie), not CSRF tokens
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
    
    let response = await fetchWithTimeout(url, {
      method: 'POST',
      headers,
      credentials: 'include', // This sends the sid cookie for authentication
      body: JSON.stringify(requestData)
    });

    console.log('📡 Response status:', response.status);
    console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

    // Handle 401/403 by attempting session refresh and retry
    if (response.status === 401 || response.status === 403) {
      console.warn('⚠️ Received 401/403 - attempting session refresh and retry...');
      
      const refreshed = await handleSessionExpired();
      
      if (refreshed) {
        // Session refreshed successfully - retry the request
        console.log('🔄 Retrying request after session refresh...');
        response = await fetchWithTimeout(url, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(requestData)
        });
        
        console.log('📡 Retry response status:', response.status);
      } else {
        // Session refresh failed, user will be logged out
        return { success: false, error: 'Session expired', sessionExpired: true };
      }
    }

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Defect data inserted successfully:', data);
      
      return {
        success: true,
        data: data?.message || data
      };
    } else {
      console.error('❌ Failed to insert defect data. Status:', response.status);
      console.error('❌ Response status text:', response.statusText);
      
      let errorText;
      let errorData = null;
      try {
        errorText = await response.text();
        console.error('❌ Error response body:', errorText);
        
        // Try to parse as JSON to get more details
        try {
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          // Response is not JSON, keep as text
        }
      } catch (e) {
        console.error('❌ Could not read error response body:', e);
        errorText = 'Unable to read error response';
      }
      
      // Check if it's specifically a CSRF error
      const isCSRFError = errorText.includes('CSRFTokenError') || 
                         errorText.includes('Invalid Request') ||
                         response.status === 403;
      
      // Return full backend error message
      return { 
        success: false, 
        error: `Server error (${response.status}): ${errorText}`,
        statusCode: response.status,
        errorData: errorData,
        isCSRFError: isCSRFError
      };
    }
  } catch (error) {
    console.error('❌ Network/fetch error inserting defect data:', error);
    console.error('❌ Error name:', error.name);
    console.error('❌ Error message:', error.message);
    
    // If it's a network error or timeout, queue the request
    if (error.message.includes('backend unavailable') || 
        error.message.includes('timeout') || 
        error.message.includes('NetworkError')) {
      
      console.log('📵 Backend unavailable during request - queueing for retry');
      const queueId = offlineApiQueue.queueApiCall('insertDefectData', {
        salesOrder,
        item,
        colour,
        fit,
        size,
        outputQty,
        repairable,
        defectIds
      });
      
      return {
        success: true,
        queued: true,
        queueId,
        message: 'Backend unavailable - API call queued for retry'
      };
    }
    
    return { 
      success: false, 
      error: error.message,
      errorType: error.name
    };
  }
};

/**
 * Initialize session and validate that sid cookie is working
 * Call this after successful login or app startup
 */
export const initializeSession = async () => {
  try {
    console.log('🔧 Validating session...');
    
    // Make a simple GET request to validate the sid cookie is working
    // Frappe uses sid cookie for REST API authentication
    try {
      const response = await fetch(`${FRAPPE_URL}/api/method/frappe.auth.get_logged_user`, {
        method: 'GET',
        credentials: 'include', // This sends the sid cookie
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Session validated for user:', data.message);
        console.log('🍪 sid cookie is working correctly');
        return { success: true, user: data.message };
      } else {
        console.warn('⚠️ Session validation failed with status:', response.status);
        return { success: false, error: `Session validation failed: ${response.status}` };
      }
    } catch (err) {
      console.warn('⚠️ Session validation request failed:', err.message);
      return { success: false, error: err.message };
    }
  } catch (error) {
    console.error('❌ Failed to validate session:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Fetch historical production data for a specific date
 * Backend determines line_no based on logged in user
 * @param {string} date - The date in YYYY-MM-DD format (e.g., "2025-11-10")
 * @returns {Promise<Array>} - Array of production items with entry_type field
 */
export const getHistoricalProductionData = async (date) => {
  try {
    const url = `${FRAPPE_URL}/api/method/pragyaa_apparels_erpnext_customizations.api.production_tracking.get_historical_production_data?date=${date}`;
    
    console.log('Fetching historical production data:', { date });
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Historical production data response:', data);
    
    // Return the message array directly
    return data?.message || [];
  } catch (error) {
    console.error('❌ Error fetching historical production data:', error);
    throw error;
  }
};
